import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AlertTriangle, Check, CheckCircle2, ChevronDown, ChevronRight, Download, HelpCircle, Loader2, User, Users, X } from "lucide-react";
import { dayLabel } from "./InkDayBar";
import { AssignMenu, KindPicker } from "./AssignMenu";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/preschool`;
const KIND_LABEL = { ind: "Individual", sib: "Sibling", grad: "Grad" };

function PhotoCard({ photo, picked, onPick, onMenu, onDrag }) {
  const review = photo.status === "review";
  const unassigned = photo.status === "unassigned";
  return (
    <div onClick={(e) => onPick(photo.i, e)} onContextMenu={(e) => onMenu(photo.i, e)}
      draggable onDragStart={(e) => onDrag(photo.i, e)}
      className={`relative rounded-md border overflow-hidden bg-slate-900 cursor-pointer select-none transition-shadow ${picked ? "ring-2 ring-amber-400 border-amber-400" : review ? "border-amber-600/70" : unassigned ? "border-slate-700" : "border-slate-800"}`}
      data-testid={`viewer-photo-${photo.i}`} data-picked={picked ? "true" : "false"}>
      {picked && <span className="absolute top-1.5 left-1.5 z-10 h-5 w-5 rounded-full bg-amber-400 text-slate-950 flex items-center justify-center" data-testid={`viewer-photo-picked-${photo.i}`}><Check className="h-3.5 w-3.5" /></span>}
      <div className="aspect-[4/3] bg-slate-950 flex items-center justify-center">
        {photo.thumb_url
          ? <img src={photo.thumb_url} alt={photo.filename} loading="lazy" className="w-full h-full object-contain" />
          : <span className="text-[10px] text-slate-600">no preview</span>}
      </div>
      <div className="p-2 space-y-1">
        <p className="text-[11px] font-mono text-slate-100 truncate" title={photo.out_name || photo.filename}>
          {review || unassigned ? photo.filename : photo.out_name}
        </p>
        <div className="flex items-center justify-between gap-1">
          <span className={`text-[10px] font-bold uppercase tracking-widest ${review ? "text-amber-300" : unassigned ? "text-slate-500" : "text-emerald-300"}`}
            data-testid={`viewer-photo-status-${photo.i}`}>
            {review ? "Review" : unassigned ? "Unassigned" : KIND_LABEL[photo.kind] || "OK"}
          </span>
          <span className="flex items-center gap-1.5">
            {photo.day && <span className="text-[9px] font-mono text-slate-500" data-testid={`viewer-photo-day-${photo.i}`}>{/^\d{4}-/.test(photo.day) ? photo.day.slice(5) : photo.day.split("/").pop()}</span>}
            {photo.sim != null && <span className="text-[10px] font-mono text-slate-500">{photo.sim.toFixed(2)}</span>}
          </span>
        </div>
        {(review || unassigned) && <p className="text-[10px] text-slate-500 truncate" title={photo.reason}>{photo.reason}</p>}
        {photo.reason?.startsWith("sheet says") && (
          <p className="text-[10px] text-cyan-300 truncate" title={photo.reason} data-testid={`viewer-photo-moved-${photo.i}`}>{photo.reason}</p>
        )}
      </div>
    </div>
  );
}

function PersonItem({ p, active, onSelect, onDropTo }) {
  const [over, setOver] = useState(false);
  const n = p.photos.length;
  const rv = p.photos.filter((x) => x.status === "review").length;
  return (
    <button type="button" onClick={() => onSelect({ type: "person", p })} data-testid={`viewer-person-${p.idx}`}
      onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); onDropTo(p, e); }}
      className={`w-full text-left pl-8 pr-3 h-8 flex items-center justify-between text-[12px] transition-colors ${over ? "bg-emerald-500/25 text-emerald-100 ring-1 ring-emerald-400" : active ? "bg-amber-400/15 text-amber-100" : "text-slate-300 hover:bg-slate-800/60"}`}>
      <span className="truncate">{p.first} {p.last}</span>
      <span className="flex items-center gap-1.5 text-[10px] font-mono">
        {rv > 0 && <span className="text-amber-300">{rv}!</span>}
        <span className={n ? "text-slate-400" : "text-red-400"}>{n}</span>
      </span>
    </button>
  );
}

function ClassNode({ c, sel, onSelect, onDropTo }) {
  const [open, setOpen] = useState(true);
  const all = [...c.children, ...c.staff];
  return (
    <div data-testid={`viewer-class-${c.name}`}>
      <button type="button" onClick={() => setOpen(!open)}
        className="w-full px-3 h-9 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest text-slate-200 hover:bg-slate-800/60">
        {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        <span className="truncate flex-1 text-left">{c.name}</span>
        <span className="text-[10px] text-slate-500 font-mono">{all.reduce((s, p) => s + p.photos.length, 0)}</span>
      </button>
      {open && (
        <>
          {c.children.length > 0 && <p className="pl-8 pt-1 text-[9px] uppercase tracking-widest text-slate-500 flex items-center gap-1"><Users className="h-3 w-3" />Names</p>}
          {c.children.map((p) => <PersonItem key={p.idx} p={p} active={sel?.p?.idx === p.idx} onSelect={onSelect} onDropTo={onDropTo} />)}
          {c.staff.length > 0 && <p className="pl-8 pt-1 text-[9px] uppercase tracking-widest text-slate-500 flex items-center gap-1"><User className="h-3 w-3" />Staff</p>}
          {c.staff.map((p) => <PersonItem key={p.idx} p={p} active={sel?.p?.idx === p.idx} onSelect={onSelect} onDropTo={onDropTo} />)}
        </>
      )}
    </div>
  );
}

function DayTabs({ days, day, onPick }) {
  if (days.length < 2) return null;
  const tab = (value, label, tid) => (
    <button key={value || "all"} type="button" onClick={() => onPick(value)} data-testid={`viewer-day-tab-${tid}`}
      className={`h-7 px-3 rounded-sm text-[10px] font-bold uppercase tracking-widest transition-colors ${day === value ? "bg-amber-400 text-slate-950" : "text-slate-300 border border-slate-700 hover:bg-slate-800"}`}>
      {label}
    </button>
  );
  return (
    <div className="px-5 py-2 border-b border-slate-800 flex items-center gap-2 shrink-0" data-testid="viewer-day-tabs">
      {tab("", "All days", "all")}
      {days.map((d, i) => tab(d.day, `${dayLabel(d, i)} (${d.count})`, i + 1))}
    </div>
  );
}

const byDay = (photos, day) => (day ? photos.filter((x) => x.day === day) : photos);
const folderOf = (x) => (x.rel_path || "").split("/").slice(0, -1).join("/");
const byFolder = (photos) => [...photos].sort((a, b) =>
  folderOf(a).localeCompare(folderOf(b), undefined, { numeric: true })
  || (a.num ?? Infinity) - (b.num ?? Infinity)
  || (a.filename || "").localeCompare(b.filename || "", undefined, { numeric: true }));

const TYPE_TABS = [["all", "All"], ["ind", "Portraits"], ["sib", "Siblings"], ["grad", "Graduates"], ["review", "Needs review"]];
const KIND_VIEW = { ind: ["All portraits", "Loose in each day folder (-P)"], sib: ["All siblings", "In each day folder's Siblings/ (-S)"], grad: ["All graduates", "In each day folder's Graduates/ (-G)"] };

function TypeTabs({ sel, counts, onPick }) {
  const active = sel?.type === "kind" ? sel.kind : sel?.type === "review" ? "review" : "all";
  return (
    <div className="flex flex-wrap gap-1.5 mb-4" data-testid="viewer-type-tabs">
      {TYPE_TABS.map(([k, label]) => (
        <button key={k} type="button" onClick={() => onPick(k)} data-testid={`viewer-type-tab-${k}`}
          className={`h-7 px-3 rounded-full text-[10px] font-bold uppercase tracking-widest transition-colors ${active === k ? "bg-amber-400 text-slate-950" : "border border-slate-700 text-slate-300 hover:bg-slate-800"}`}>
          {label}{counts[k] != null ? ` · ${counts[k]}` : ""}
        </button>
      ))}
    </div>
  );
}

export function ResultsViewer({ task, onClose, onDownload, onApprove }) {
  const [raw, setRaw] = useState(null);
  const [sel, setSel] = useState(null);
  const [day, setDay] = useState("");
  const [status, setStatus] = useState(task.status);
  const [picked, setPicked] = useState(new Set());
  const [anchor, setAnchor] = useState(null);
  const [menu, setMenu] = useState(null);
  const [drop, setDrop] = useState(null);
  const paused = status === "match_review";
  const data = useMemo(() => raw && {
    ...raw,
    review: byFolder(byDay(raw.review, day)),
    unassigned: byFolder(byDay(raw.unassigned, day)),
    classes: raw.classes.map((c) => ({
      ...c,
      children: c.children.map((p) => ({ ...p, photos: byDay(p.photos, day) })),
      staff: c.staff.map((p) => ({ ...p, photos: byDay(p.photos, day) })),
    })),
  }, [raw, day]);

  const load = useCallback((keepSel) => axios.get(`${API}/tasks/${task.task_id}/results`).then(({ data: d }) => {
    setRaw(d);
    const first = d.classes[0]?.children[0] || d.classes[0]?.staff[0];
    if (!keepSel && first) setSel({ type: "person", p: first });
  }).catch((e) => toast.error(e.response?.data?.detail || "Couldn't load results")), [task.task_id]);
  useEffect(() => { load(false); }, [load]);
  useEffect(() => { setPicked(new Set()); setAnchor(null); }, [sel, day]);

  const byKind = useMemo(() => {
    const out = { ind: [], sib: [], grad: [] };
    if (!data) return out;
    const seen = new Set();
    data.classes.forEach((c) => [...c.children, ...c.staff].forEach((p) => p.photos.forEach((ph) => {
      if (ph.status !== "ok" || seen.has(ph.i) || !out[ph.kind]) return;
      seen.add(ph.i);
      out[ph.kind].push(ph);
    })));
    Object.keys(out).forEach((k) => { out[k] = byFolder(out[k]); });
    return out;
  }, [data]);
  const view = useMemo(() => {
    if (!data || !sel) return null;
    if (sel.type === "review") return { title: "Needs review", sub: "Moved to _review/ in the ZIP with original names", photos: data.review };
    if (sel.type === "kind") {
      const [title, sub] = KIND_VIEW[sel.kind];
      return { title, sub, photos: byKind[sel.kind] };
    }
    if (sel.type === "unassigned") return { title: "Unassigned", sub: "No written number covers these photos — in _unassigned/", photos: data.unassigned };
    const p = data.classes.flatMap((c) => [...c.children, ...c.staff]).find((x) => x.idx === sel.p.idx) || sel.p;
    const nums = Object.entries(p.numbers).filter(([, v]) => v.length).map(([k, v]) => `${k.toUpperCase()} ${v.join(", ")}`).join(" · ");
    return { title: `${p.first} ${p.last}`, sub: `${p.is_staff ? `Staff · ${p.staff_alloc || "unallocated"}` : p.room}${nums ? ` · ${nums}` : " · no numbers"}`, photos: p.photos };
  }, [data, sel, byKind]);
  const pickType = (k) => {
    if (k === "review") return setSel({ type: "review" });
    if (k !== "all") return setSel({ type: "kind", kind: k });
    const first = data.classes[0]?.children[0] || data.classes[0]?.staff[0];
    if (first) setSel({ type: "person", p: first });
  };

  const order = view ? view.photos.map((x) => x.i) : [];
  const onPick = (i, e) => {
    setPicked((prev) => {
      if (e.shiftKey && anchor != null) {
        const [a, b] = [order.indexOf(anchor), order.indexOf(i)].sort((x, y) => x - y);
        return new Set([...prev, ...order.slice(a, b + 1)]);
      }
      if (e.ctrlKey || e.metaKey) {
        const n = new Set(prev);
        n.has(i) ? n.delete(i) : n.add(i);
        return n;
      }
      return prev.size === 1 && prev.has(i) ? new Set() : new Set([i]);
    });
    if (!e.shiftKey) setAnchor(i);
  };
  const onMenu = (i, e) => {
    if (!e.ctrlKey && !e.metaKey) return;
    e.preventDefault();
    if (!picked.has(i)) setPicked((prev) => new Set([...prev, i]));
    setMenu({ x: e.clientX, y: e.clientY });
  };
  const send = async (body, msg) => {
    const items = [...picked];
    try {
      const { data: r } = await axios.post(`${API}/tasks/${task.task_id}/assign`, { items, ...body });
      setStatus(r.status);
      toast.success(msg(r.changed));
      setMenu(null);
      setPicked(new Set());
      await load(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't move the photos");
    }
  };
  const assign = (pid, kind) => {
    const p = raw.classes.flatMap((c) => [...c.children, ...c.staff]).find((x) => x.idx === pid);
    send({ person_idx: pid, kind }, (n) => `${n} photo${n > 1 ? "s" : ""} → ${p.first} ${p.last}`);
  };
  const onDrag = (i, e) => {
    const items = picked.has(i) ? [...picked] : [i];
    if (!picked.has(i)) setPicked(new Set([i]));
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", items.join(","));
  };
  const onDropTo = (p, e) => {
    const items = (e.dataTransfer.getData("text/plain") || "").split(",").filter(Boolean).map(Number);
    if (!items.length) return;
    setPicked(new Set(items));
    setDrop({ p, x: e.clientX, y: e.clientY });
  };
  const addPerson = async (person, kind) => {
    try {
      const { data: r } = await axios.post(`${API}/tasks/${task.task_id}/people`, person);
      await send({ person_idx: r.person.idx, kind }, (n) => `Added ${person.first} ${person.last} · ${n} photo${n > 1 ? "s" : ""} assigned`);
    } catch (e) {
      const same = (a, b) => (a || "").trim().toLowerCase() === (b || "").trim().toLowerCase();
      const hit = e.response?.status === 409 && raw.classes.find((c) => same(c.name, person.room))?.[person.is_staff ? "staff" : "children"]
        .find((p) => same(p.first, person.first) && same(p.last, person.last));
      if (hit) await send({ person_idx: hit.idx, kind }, (n) => `${person.first} ${person.last} already existed · ${n} photo${n > 1 ? "s" : ""} assigned`);
      else toast.error(e.response?.data?.detail || "Couldn't add that person");
    }
  };
  const clearManual = async () => {
    if (!window.confirm(`Forget all ${raw.manual_count} manual fixes and re-run matching from scratch?`)) return;
    try {
      await axios.delete(`${API}/tasks/${task.task_id}/manual`);
      toast.success("Manual fixes cleared — re-matching");
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't clear the manual fixes");
    }
  };
  const backToReview = () => send({ action: "review" }, (n) => `${n} photo${n > 1 ? "s" : ""} sent back to review`);

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="results-viewer">
      <div className="w-[96vw] h-[92vh] bg-slate-950 border border-slate-800 rounded-md flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-5 h-14 border-b border-slate-800 shrink-0">
          <div>
            <h2 className="text-sm font-bold text-slate-100">{task.name}</h2>
            <p className="text-[11px] text-slate-500 font-mono" data-testid="viewer-header-counts">{(raw?.task || task).num_ok} named · {(raw?.task || task).num_review} review · {(raw?.task || task).num_unassigned} unassigned
            {raw?.manual_count > 0 && (
              <span className="ml-3 text-amber-200" data-testid="viewer-manual-count">· {raw.manual_count} manual fix{raw.manual_count > 1 ? "es" : ""} kept on re-run
                <button type="button" onClick={clearManual} data-testid="viewer-clear-manual-btn"
                  className="ml-2 underline underline-offset-2 text-slate-400 hover:text-red-300">Clear my manual fixes</button>
              </span>
            )}</p>
          </div>
          <div className="flex items-center gap-2">
            {picked.size > 0 && (
              <button type="button" onClick={(e) => setMenu({ x: e.clientX - 260, y: e.clientY + 16 })} data-testid="viewer-assign-selected-btn"
                className="h-9 px-3 rounded-sm border border-amber-500 text-amber-200 text-[11px] font-bold uppercase tracking-widest hover:bg-amber-900/30">
                Assign {picked.size} selected
              </button>
            )}
            {paused ? (
              <button type="button" onClick={() => onApprove({ ...task, status })} data-testid="viewer-approve-btn"
                className="h-9 px-4 inline-flex items-center gap-1.5 rounded-sm bg-emerald-500 text-slate-950 text-[11px] font-bold uppercase tracking-widest hover:bg-emerald-400">
                <CheckCircle2 className="h-3.5 w-3.5" />Approve &amp; build ZIP
              </button>
            ) : (
              <button type="button" onClick={() => onDownload(task)} data-testid="viewer-download-btn"
                className="h-9 px-4 inline-flex items-center gap-1.5 rounded-sm bg-emerald-500 text-slate-950 text-[11px] font-bold uppercase tracking-widest hover:bg-emerald-400">
                <Download className="h-3.5 w-3.5" />Download ZIP
              </button>
            )}
            <button type="button" onClick={onClose} className="text-slate-500 hover:text-slate-100" data-testid="results-viewer-close"><X className="h-5 w-5" /></button>
          </div>
        </div>
        {paused && <p className="px-5 py-1.5 text-[11px] text-amber-200 bg-amber-950/30 border-b border-amber-800/40" data-testid="viewer-paused-note">Paused before the ZIP — check each day, fix numbers if needed (re-runs matching), then approve. Click / Ctrl / Shift-click photos, then Ctrl+Right-click — or drag them onto a name — to assign them to a child.</p>}
        {raw && <DayTabs days={raw.days || []} day={day} onPick={setDay} />}
        {!data ? (
          <div className="flex-1 flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-amber-400" /></div>
        ) : (
          <div className="flex-1 flex min-h-0">
            <aside className="w-72 shrink-0 border-r border-slate-800 overflow-y-auto py-2" data-testid="viewer-tree">
              {data.classes.map((c) => <ClassNode key={c.name} c={c} sel={sel} onSelect={setSel} onDropTo={onDropTo} />)}
              <div className="mt-2 border-t border-slate-800 pt-2">
                <button type="button" onClick={() => setSel({ type: "review" })} data-testid="viewer-review-node"
                  className={`w-full px-3 h-9 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest ${sel?.type === "review" ? "bg-amber-400/15 text-amber-100" : "text-amber-300 hover:bg-slate-800/60"}`}>
                  <AlertTriangle className="h-3.5 w-3.5" /><span className="flex-1 text-left">Needs review</span><span className="font-mono text-[10px]">{data.review.length}</span>
                </button>
                <button type="button" onClick={() => setSel({ type: "unassigned" })} data-testid="viewer-unassigned-node"
                  className={`w-full px-3 h-9 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest ${sel?.type === "unassigned" ? "bg-slate-700/40 text-slate-100" : "text-slate-400 hover:bg-slate-800/60"}`}>
                  <HelpCircle className="h-3.5 w-3.5" /><span className="flex-1 text-left">Unassigned</span><span className="font-mono text-[10px]">{data.unassigned.length}</span>
                </button>
              </div>
            </aside>
            <section className="flex-1 overflow-y-auto p-5" data-testid="viewer-photos-panel">
              {view && (
                <>
                  <TypeTabs sel={sel} onPick={pickType}
                    counts={{ ind: byKind.ind.length, sib: byKind.sib.length, grad: byKind.grad.length, review: data.review.length }} />
                  <h3 className="text-lg font-bold text-slate-100" data-testid="viewer-selected-title">{view.title}</h3>
                  <p className="text-[11px] text-slate-500 mb-4 font-mono">{view.sub}</p>
                  {view.photos.length === 0
                    ? <p className="text-[12px] text-slate-500" data-testid="viewer-empty">No photos.</p>
                    : <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">{view.photos.map((ph) => <PhotoCard key={`${sel.type}-${ph.i}`} photo={ph} picked={picked.has(ph.i)} onPick={onPick} onMenu={onMenu} onDrag={onDrag} />)}</div>}
                </>
              )}
            </section>
          </div>
        )}
      </div>
      {drop && (
        <KindPicker at={drop} count={picked.size} onPick={(kind) => { assign(drop.p.idx, kind); setDrop(null); }} onClose={() => setDrop(null)} />
      )}
      {menu && raw && (
        <AssignMenu at={menu} count={picked.size} classes={raw.classes} onAssign={assign} onAddPerson={addPerson} onBack={backToReview} onClose={() => setMenu(null)} />
      )}
    </div>
  );
}
